import {
  Directive, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output
} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs/Subscription';
import {
  DealPayrollBreakdown, hasExactDealPayrollMemoryMatch, isDealPayrollIdentityMatch,
  isPlanfixDealPayroll
} from '../../../models/deal-payroll.model';
import {
  dealPayrollSalaryPresentation, hasTrustedDealPayrollModifiers,
  markDealPayrollModifiersRemote
} from '../../../models/deal-payroll-report-presentation';
import {Report} from '../../../models/report.model';
import {ReportService} from '../../../services/report.service';
import {
  DETAIL_MODAL_KEYBOARD, DETAIL_MODAL_SIZE, DETAIL_TRIGGER_LABEL_KEY,
  DETAIL_TRIGGER_POPUP, DETAIL_TRIGGER_ROLE, DETAIL_TRIGGER_TAB_INDEX
} from './deal-payroll-detail-trigger.constants';
import {DealPayrollDetailComponent} from './deal-payroll-detail.component';

@Directive({selector: '[dealPayrollDetailTrigger]'})
export class DealPayrollDetailTriggerDirective implements OnDestroy {
  @HostBinding('attr.role') role: string = null;
  @HostBinding('attr.tabindex') tabIndex: number = null;
  @HostBinding('attr.aria-label') ariaLabel: string = null;
  @HostBinding('attr.aria-haspopup') ariaHasPopup: string = null;
  @HostBinding('attr.title') title: string = null;
  @Output() dealPayrollBreakdownLoaded = new EventEmitter<DealPayrollBreakdown>();

  private report: Report;
  private allowed = false;
  private breakdownSubscription: Subscription;

  constructor(
    private modalService: NgbModal,
    private reportService: ReportService,
    private translate: TranslateService
  ) {
  }

  @Input()
  set dealPayrollDetailTrigger(report: Report) {
    this.report = report;
    this.updateTriggerContext();
  }

  @Input()
  set dealPayrollDetailAllowed(allowed: boolean) {
    this.allowed = allowed === true;
    this.updateTriggerContext();
  }

  private updateTriggerContext(): void {
    const expectedMonth = this.reportService.getCurrentDealPayrollMonth();
    const breakdown = this.report && this.report.dealPayrollBreakdown;
    const interactive = this.allowed && !!this.report && isPlanfixDealPayroll(this.report)
      && !!this.report.userID && !!expectedMonth;
    const exactIdentity = interactive
      && isDealPayrollIdentityMatch(this.report.userID, expectedMonth, breakdown);
    const label = interactive
      ? this.translate.instant(exactIdentity
        ? dealPayrollSalaryPresentation(this.report).labelKey
        : DETAIL_TRIGGER_LABEL_KEY)
      : null;
    this.role = interactive ? DETAIL_TRIGGER_ROLE : null;
    this.tabIndex = interactive ? DETAIL_TRIGGER_TAB_INDEX : null;
    this.ariaLabel = label;
    this.ariaHasPopup = interactive ? DETAIL_TRIGGER_POPUP : null;
    this.title = label;
  }

  ngOnDestroy(): void {
    this.cancelBreakdownSubscription();
  }

  @HostListener('click', ['$event'])
  openDetails(event?: Event): void {
    if (!this.role) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    const modal = this.modalService.open(DealPayrollDetailComponent, {
      size: DETAIL_MODAL_SIZE,
      keyboard: DETAIL_MODAL_KEYBOARD
    });
    this.assignModalContext(modal);
    this.cancelBreakdownSubscription();
    this.breakdownSubscription = modal.componentInstance.breakdownLoaded.subscribe(
      (breakdown: DealPayrollBreakdown) => this.forwardRemoteBreakdown(breakdown));
  }

  @HostListener('keydown.enter', ['$event'])
  openDetailsFromEnter(event: KeyboardEvent): void {
    if (this.role) {
      event.preventDefault();
      this.openDetails(event);
    }
  }

  @HostListener('keydown.space', ['$event'])
  openDetailsFromSpace(event: KeyboardEvent): void {
    if (this.role) {
      event.preventDefault();
      this.openDetails(event);
    }
  }

  private assignModalContext(modal: NgbModalRef): void {
    const breakdown = this.report.dealPayrollBreakdown;
    const userId = String(this.report.userID);
    const month = this.reportService.getCurrentDealPayrollMonth();
    const exactIdentity = isDealPayrollIdentityMatch(userId, month, breakdown);
    const remembered = this.reportService.getDealPayrollBreakdown(userId, month);
    modal.componentInstance.userId = userId;
    modal.componentInstance.month = month;
    modal.componentInstance.calculation = (this.report.howSalaryCalculated || []).slice();
    modal.componentInstance.calculationTrusted = hasTrustedDealPayrollModifiers(this.report);
    modal.componentInstance.initialBreakdown = exactIdentity
      ? this.breakdownWithRememberedTasks(breakdown, remembered)
      : undefined;
  }

  private forwardRemoteBreakdown(breakdown: DealPayrollBreakdown): void {
    markDealPayrollModifiersRemote(this.report);
    this.dealPayrollBreakdownLoaded.emit(breakdown);
  }

  private breakdownWithRememberedTasks(
    breakdown: DealPayrollBreakdown,
    remembered: DealPayrollBreakdown
  ): DealPayrollBreakdown {
    return hasExactDealPayrollMemoryMatch(breakdown, remembered)
      ? Object.assign({}, breakdown, {taskLines: remembered.taskLines.slice()})
      : breakdown;
  }

  private cancelBreakdownSubscription(): void {
    if (this.breakdownSubscription) {
      this.breakdownSubscription.unsubscribe();
      this.breakdownSubscription = undefined;
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail-trigger.directive.ts